import React from 'react';
import { useAuthState, useAuthActions } from '@/utils/auth';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
// import { PhoneInputField } from '@/components/Inputs/PhoneInput';
import { makeDefaultValues } from '@/utils/makeDefaultValues';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User } from '@/generated/codegen';
import { Button } from '@/components/ui/button';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { FieldError } from '@/components/ui/field';
import { FormFields } from '@/components/Inputs/FormFields';
import { IncomingMergeDialog } from '@/modules/identity_claim/IdentityClaimEntry';
import { isMobileApp } from '@/utils/isMobileApp';

const fieldCreator = makeFormFieldCreator<User>();

// El email es el de la identidad (Google) y no viaja en el submit: se muestra
// para que la persona sepa con qué cuenta entró, pero no se edita acá.
const partialProfileFields = [
  fieldCreator({
    name: 'email',
    label: 'email',
    disabled: true,
    inputProps: { type: 'email' },
  }),
  fieldCreator({
    name: 'dni',
    label: 'DNI',
  }),
  fieldCreator({
    name: 'first_name',
    label: 'Nombre',
    inputProps: { autoComplete: 'given-name' },
  }),
  fieldCreator({
    name: 'last_name',
    label: 'Apellido',
    inputProps: { autoComplete: 'family-name' },
  }),
];

const defaultValues = makeDefaultValues(partialProfileFields);

const partialProfileSchema = Yup.object().shape({
  dni: Yup.string()
    .matches(/^[0-9.]*$/, 'Ingresa un DNI valido.')
    .required('Campo obligatorio')
    .typeError('Campo obligatorio'),
  first_name: Yup.string().required('Campo obligatorio'),
  last_name: Yup.string().required('Campo obligatorio'),
  // phone: Yup.string().phoneNumber(),
});

export const AuthCompleteProfile = () => {
  const { error, partialProfile, loading } = useAuthState();
  const { signOut, submit } = useAuthActions();

  // BIL-3684: Sanitize any string value equal to 'unknown' coming from partialProfile
  const incomingMerge = partialProfile?.incoming_merge;
  const sanitizedPartialProfile = React.useMemo(() => {
    if (!partialProfile) return partialProfile as typeof partialProfile;
    const { incoming_merge: _incomingMerge, ...profile } = partialProfile;
    return Object.fromEntries(
      Object.entries(profile).map(([k, v]) => [k, v === 'unknown' ? '' : v])
    ) as typeof partialProfile;
  }, [partialProfile]);

  const initialValues = React.useMemo(
    () => ({ ...defaultValues, ...sanitizedPartialProfile, dni: '' }),
    [sanitizedPartialProfile]
  );

  return (
    <>
      {incomingMerge && isMobileApp() && (
        <IncomingMergeDialog incoming={incomingMerge} />
      )}
      <CardHeader>
        <CardTitle>Completa tu perfil</CardTitle>
      </CardHeader>
      <CardContent style={{ width: '100%' }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={partialProfileSchema}
          onSubmit={(values) => {
            submit(values);
          }}
        >
          {() => (
            <Form className="grid gap-4">
              <FormFields fields={partialProfileFields} />
              {error && (
                <FieldError className="block">{error?.message}</FieldError>
              )}
              <hr />
              <Button
                size="lg"
                className="block"
                disabled={loading}
                type="submit"
              >
                {loading ? <LoadingSpinner /> : 'Enviar'}
              </Button>
              <div className="text-center pt-1">
                <h6 style={{ marginTop: '0.5rem' }}>o</h6>
                <h6 className="m-0">
                  <Button onClick={() => signOut()} variant="link">
                    Salir
                  </Button>
                </h6>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </>
  );
};
